import type { PoiData } from '$lib/components/pois/types';
import type { LinkSegments } from '$lib/components/seo/types';
import type { AvailableLanguageTag } from '$paraglide/runtime';
import { dev } from '$app/environment';
import { getSegmentsLabel, segments2href } from '$lib/components/seo/linkSegments';
import { i18nPath } from '$lib/i18n/routing';
import { escape } from 'html-escaper';

export function getAddressSegmentsLinks(
	addressSegments: LinkSegments,
	lang: AvailableLanguageTag,
): string {
	return addressSegments
		.map((_, index) => {
			const addressSegmentsSlice = addressSegments.slice(0, index + 1);
			const href = escape(segments2href(addressSegmentsSlice, lang, ['map']));
			const label = escape(getSegmentsLabel(addressSegmentsSlice));

			return `<a href="${href}">${label}</a>`;
		})
		.join(' ');
}

export function getHotelLinks(hotels: PoiData[], lang: AvailableLanguageTag) {
	return hotels.map((hotel) => {
		const escapedIdentifier = escape(hotel.urlIdentifier);
		const href = i18nPath(`/map/ACCOMMODATION/poi/${escapedIdentifier}`, lang);
		const name = escape(hotel.name);

		return `<a href="${href}">${name}</a>`;
	});
}

export function getAddressSegmentsLabels(addressSegments: LinkSegments): string {
	return addressSegments
		.map((_, index) => {
			const addressSegmentsSlice = addressSegments.slice(0, index + 1);

			return getSegmentsLabel(addressSegmentsSlice);
		})
		.join(' ');
}

/**
 * Replace all occurrences of the keys in the text with the corresponding values
 * This function also ensures all the text is html escaped
 *
 * The verify option ensures that all the placeholders in the replaced text were actually replaced.
 * If verify is set to false, make sure to verify the text after calling this function
 */
export function replaceText(text: string, entries: Record<string, string>, verify = true) {
	const newText = Object.entries(entries).reduce((acc, [key, value]) => {
		return acc.replaceAll(`{{${key}}}`, escape(value));
	}, text);

	if (dev && verify && newText.includes('{{')) {
		console.error('Some variables were not replaced', newText);
	}

	return newText;
}

/**
 * Replace all occurrences of the keys in the text with the corresponding values
 * This function does NOT ensure that all the text is html escaped so this should be done before calling this function
 *
 * The verify option ensures that all the placeholders in the replaced text were actually replaced.
 * If verify is set to false, make sure to verify the text after calling this function
 */
export function replaceHtml(text: string, entries: Record<string, string>, verify = true) {
	const newText = Object.entries(entries).reduce((acc, [key, value]) => {
		return acc.replaceAll(`{{${key}}}`, value);
	}, text);

	if (dev && verify && newText.includes('{{')) {
		console.error('Some variables were not replaced', newText);
	}

	return newText;
}
